import { isFedRamp } from '@atlassian/atl-context';
import type { AIDefinitionsPluginConfig } from '@atlassian/editor-plugin-ai-definitions';

import { useQueryHighlightAcronyms } from '@confluence/contextual-reading-aids/entry-points/RendererTextHighlighter';
import { READING_AIDS_MAX_WORD_COUNT } from '@confluence/contextual-reading-aids/entry-points/reading-aids-utils';
import { ReadingAidsPopup } from '@confluence/contextual-reading-aids/entry-points/readingAidsPopup';
import { usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';

/**
 * The hook to check whether the AI Definitions plugin should be enabled

The AI Definitions Define button is enabled in `confluence/next/packages/highlight-actions/src/HighlightActions.tsx`
```ts
  const showReadingAidsDefineButton = isAIEnabledForContent || isRovoEnabledForContent;
```

The AI Definitions auto-highlight is enabled in `confluence/next/packages/view-page-common/src/ViewPageCommon.tsx`
```ts
	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) &&
		locale.startsWith('en');
```

The AI Definitions auto-highlight skipNetworkCall for acronymRegex is defined in `confluence/next/packages/content-renderer/src/renderers/PageContentRendererFabric.tsx`
```ts
  const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) &&
		locale.startsWith('en') &&
		!JSON.parse(
			localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT) || '""',
		);
```

 * So, the AI Definitions plugin should be enabled when `isAIEnabledForContent || isRovoEnabledForContent` is true.
 * But, the auto-highlight should only be enabled when `isReadingAidsAutoHighlightEnabled` is true for `PageContentRendererFabric`.
 * - `isReadingAidsAutoHighlightEnabled` will be passed to the AI Definitions plugin as `autoHighlightEnabled`.
 *
 * @returns whether the AI Definitions plugin should be enabled
 */
export const useEditorPluginAIDefinitionsEnabled = (): boolean => {
	/**
	 * see comments in `AIDefinitionsPluginConfig` for more details about why not checking `isLivePage` here
	 * const isLivePage = useIsCurrentPageLive();
	 */

	const [contentId] = usePageContentId();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	// use a separate variable to help debug
	const notFedRamp = !isFedRamp();

	const featureEnabled = isAIEnabledForContent || isRovoEnabledForContent;

	return notFedRamp && featureEnabled;
};

/**
 * The hook to check whether the AI Definitions auto-highlight should be enabled
 *
 * `isReadingAidsAutoHighlightEnabled` is from `confluence/next/packages/content-renderer/src/renderers/PageContentRendererFabric.tsx`
 *
 * Note that the `localStorage` is from `@confluence/storage-manager`
 *
 * @returns whether the AI Definitions auto-highlight should be enabled
 */
export const useEditorPluginAIDefinitionsAutoHighlightEnabled = (): boolean => {
	const { locale } = useSessionData();
	const [contentId] = usePageContentId();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});

	return (
		(isAIEnabledForContent || isRovoEnabledForContent) &&
		locale.startsWith('en') &&
		!JSON.parse(
			localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT) || '""',
		)
	);
};

/**
 * The hook to get the configuration for the AI Definitions plugin
 *
 * see comments in `AIDefinitionsPluginConfig` for more details about why not passing `isLivePage` here
 * const isLivePage = useIsCurrentPageLive();
 *
 * @returns the configuration for the AI Definitions plugin
 */
export const useEditorPluginAIDefinitionsConfig = (): AIDefinitionsPluginConfig => {
	const [contentId] = usePageContentId();
	const enabled = useEditorPluginAIDefinitionsEnabled();
	const autoHighlightEnabled = useEditorPluginAIDefinitionsAutoHighlightEnabled();
	const { queryHighlightAcronyms } = useQueryHighlightAcronyms();
	const { isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const enablementSource = isRovoEnabledForContent ? 'rovo' : 'general-ai';

	return {
		enabled,
		contentId,
		autoHighlightEnabled,
		queryHighlightAcronyms,
		ReadingAidsPopup,
		enablementSource,
		readingAidsMaxWordCount: READING_AIDS_MAX_WORD_COUNT,
	};
};
